/**
 * The record's address and its share links — the member-side panel on the
 * Official Record page.
 *
 * Shows the permanent address, opens the paper register, and (when the
 * workspace has opted in to the Record Address module) mints and revokes
 * scoped share tokens. Minting authority and the opt-in are enforced
 * server-side (migration 721); this panel only makes the outcome visible.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, ExternalLink, Link2, Loader2, Plus, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Field, Select, TextInput } from '../ds';
import {
  SHARE_SCOPE_LABELS,
  absoluteUrl,
  createShareToken,
  listShareTokens,
  recordAddress,
  revokeShareToken,
  tokenAddress,
  type PorShareToken,
  type ShareScope,
} from '../../lib/porShareService';
import type { PorRecordSnapshot } from '../../lib/planOfRecordService';
import type { AssembledPor } from '../../types/plan-of-record';
import { formatDate } from './porUi';

const EXPIRY_OPTIONS = [
  { value: '', label: 'Never expires' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];

function useCopy(): [string | null, (key: string, text: string) => void] {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = useCallback((key: string, text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(key);
        window.setTimeout(() => setCopied((k) => (k === key ? null : k)), 1600);
      })
      .catch(() => {});
  }, []);
  return [copied, copy];
}

export function PorAddressPanel({
  porId,
  snapshot,
  versions,
}: {
  porId: string;
  snapshot: PorRecordSnapshot;
  versions: Array<Pick<PorRecordSnapshot, 'version' | 'authorized_at'>>;
}) {
  const [tokens, setTokens] = useState<PorShareToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [minting, setMinting] = useState(false);
  const [scope, setScope] = useState<ShareScope>('record');
  const [version, setVersion] = useState<string>(String(snapshot.version));
  const [commitmentId, setCommitmentId] = useState<string>('');
  const [label, setLabel] = useState('');
  const [expiryDays, setExpiryDays] = useState('');
  const [minted, setMinted] = useState<string | null>(null);
  const [copied, copy] = useCopy();

  const address = recordAddress(porId);
  const payload = snapshot.payload as AssembledPor;
  const commitments = useMemo(() => (payload?.commitments ?? []).slice().sort((a, b) => a.sort_order - b.sort_order), [payload]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: e } = await listShareTokens(porId);
    setTokens(data);
    setError(e);
    setLoading(false);
  }, [porId]);

  useEffect(() => {
    void load();
  }, [load]);

  const mint = async () => {
    setMinting(true);
    setError(null);
    setMinted(null);
    const expiresAt = expiryDays ? new Date(Date.now() + Number(expiryDays) * 86_400_000).toISOString() : null;
    const { data, error: e } = await createShareToken({
      porId,
      scope,
      version: scope === 'record' ? null : Number(version),
      commitmentId: scope === 'commitment' ? commitmentId || null : null,
      label: label || null,
      expiresAt,
    });
    setMinting(false);
    if (e || !data) {
      setError(e ?? 'Could not create share link.');
      return;
    }
    setMinted(absoluteUrl(data.address));
    setShowForm(false);
    setLabel('');
    await load();
  };

  const revoke = async (t: PorShareToken) => {
    if (!confirm(`Withdraw this share link${t.label ? ` ("${t.label}")` : ''}? Anyone holding it loses access immediately.`)) return;
    const { error: e } = await revokeShareToken(t.id);
    if (e) setError(e);
    await load();
  };

  const live = tokens.filter((t) => !t.revokedAt && (!t.expiresAt || new Date(t.expiresAt) > new Date()));
  const spent = tokens.filter((t) => !live.includes(t));

  return (
    <section className="border border-[var(--rule)] bg-[var(--bg-raised)] print:hidden">
      <div className="px-5 py-3 border-b border-[var(--rule)] flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--text-ter)]">The address</div>
          <div className="flex items-center gap-2 mt-0.5 min-w-0">
            <code className="text-sm text-[var(--text-pri)] truncate">{address}</code>
            <Button variant="ghost" size="sm" className="!p-1" title="Copy the address" onClick={() => copy('address', absoluteUrl(address))}>
              {copied === 'address' ? <Check className="h-3.5 w-3.5 text-[var(--green)]" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
          <p className="text-xs text-[var(--text-ter)] mt-1">
            Permanent. <code>/v/{snapshot.version}</code> pins this version; <code>/c/&lt;commitment&gt;</code> opens one commitment. Members read it signed in; anyone else needs an account and a share link.
          </p>
        </div>
        <Link to={address} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700">
          <ExternalLink className="h-4 w-4" /> Open the paper register
        </Link>
      </div>

      <div className="px-5 py-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-[var(--text-pri)] inline-flex items-center gap-1.5">
            <Link2 className="h-4 w-4 text-[var(--text-ter)]" /> Share links
            <span className="font-mono text-[10px] text-[var(--text-ter)] ml-1">{live.length} live</span>
          </div>
          {!showForm && (
            <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>
              <Plus className="h-3.5 w-3.5" /> New share link
            </Button>
          )}
        </div>
        <p className="text-xs text-[var(--text-ter)]">
          View-only and revocable at any time. A link works for anyone who has it and is signed in to an account — it does not open for someone who is signed out. Only ratified versions can be shared; the reader never sees names or emails, only the record. Every link minted or withdrawn is written to the change log.
        </p>

        {minted && (
          <div className="border border-[var(--green)] bg-[var(--green-dim)] px-3 py-2 text-sm flex flex-wrap items-center justify-between gap-2">
            <span className="truncate font-mono text-xs">{minted}</span>
            <Button variant="secondary" size="sm" onClick={() => copy('minted', minted)}>
              {copied === 'minted' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copy link
            </Button>
          </div>
        )}

        {showForm && (
          <div className="border border-[var(--rule)] p-3 space-y-3 bg-[var(--surface)]/60">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Scope">
                <Select
                  value={scope}
                  onChange={(e) => setScope(e.target.value as ShareScope)}
                  options={(Object.keys(SHARE_SCOPE_LABELS) as ShareScope[]).map((k) => ({ value: k, label: SHARE_SCOPE_LABELS[k] }))}
                />
              </Field>
              <Field label="Expires">
                <Select value={expiryDays} onChange={(e) => setExpiryDays(e.target.value)} options={EXPIRY_OPTIONS} />
              </Field>
              {scope !== 'record' && (
                <Field label="Version">
                  <Select
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    options={versions.map((v) => ({ value: String(v.version), label: `v${v.version} · ${formatDate(v.authorized_at)}` }))}
                  />
                </Field>
              )}
              {scope === 'commitment' && (
                <Field label="Commitment" hint="From the version being shared.">
                  <Select
                    value={commitmentId}
                    onChange={(e) => setCommitmentId(e.target.value)}
                    placeholder="Pick a commitment"
                    options={commitments.map((c) => ({ value: c.id, label: c.title }))}
                  />
                </Field>
              )}
              <Field label="Label" hint="Who it is for — shown in the change log.">
                <TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Board pack, Q4" />
              </Field>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button
                variant="primary"
                size="sm"
                disabled={minting || (scope === 'commitment' && !commitmentId)}
                onClick={() => void mint()}
              >
                {minting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Create link
              </Button>
            </div>
          </div>
        )}

        {error && <p className="text-xs text-[var(--coral)]">{error}</p>}

        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-[var(--text-ter)]" />
        ) : live.length === 0 && spent.length === 0 ? (
          <p className="text-xs text-[var(--text-ter)]">No share links yet.</p>
        ) : (
          <div className="divide-y divide-[var(--rule)] border border-[var(--rule)]">
            {[...live, ...spent].map((t) => {
              const dead = !live.includes(t);
              const path = tokenAddress(t);
              return (
                <div key={t.id} className={`px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-xs ${dead ? 'opacity-60' : ''}`}>
                  <div className="min-w-0">
                    <div className="text-[var(--text-pri)] font-medium truncate">
                      {t.label ?? 'Untitled link'}
                      <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-[var(--text-ter)]">
                        {t.scope}{t.version != null ? ` v${t.version}` : ''}
                      </span>
                    </div>
                    <div className="text-[var(--text-ter)]">
                      Created {formatDate(t.createdAt)} · {t.viewCount} view{t.viewCount === 1 ? '' : 's'}
                      {t.lastViewedAt ? `, last ${formatDate(t.lastViewedAt)}` : ''}
                      {t.expiresAt ? ` · expires ${formatDate(t.expiresAt)}` : ''}
                      {t.revokedAt ? ` · withdrawn ${formatDate(t.revokedAt)}` : ''}
                    </div>
                  </div>
                  {!dead && (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="!px-2 !py-1 !text-xs" onClick={() => copy(t.id, absoluteUrl(path))}>
                        {copied === t.id ? <Check className="h-3.5 w-3.5 text-[var(--green)]" /> : <Copy className="h-3.5 w-3.5" />} Copy
                      </Button>
                      <Button variant="ghost" size="sm" className="!px-2 !py-1 !text-xs text-[var(--coral)]" onClick={() => void revoke(t)}>
                        <X className="h-3.5 w-3.5" /> Withdraw
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export default PorAddressPanel;
